import type { IGraphClient } from '../../../src/services/GraphService';
import { findListId } from './findList';

/** A column in one of our lists. `name` is the internal name and never changes; `label` is what people see. */
export interface IListColumn {
  name: string;
  label: string;
  kind: 'text' | 'note' | 'date' | 'dateTime' | 'number' | 'boolean' | 'choice';
  choices?: string[];
  /** Indexed columns are the only ones we filter on, so queries stay fast on big lists. */
  indexed?: boolean;
  hidden?: boolean;
}

/**
 * One of our lists in the firm's site. `urlName` sets the list's address and never changes;
 * `title` is what people see. Names are product-neutral on purpose: the product name may change.
 */
export interface IListSchema {
  urlName: string;
  title: string;
  /** What the list's own Title column is shown as (e.g. "Client"); every row's Title says what it is. */
  titleLabel: string;
  columns: IListColumn[];
}

export type FieldValue = string | number | boolean | undefined;
export interface IFields { [column: string]: FieldValue }

/** A row: SharePoint's item id, our stable Key, and its values. */
export interface IRecord {
  id: string;
  key: string;
  fields: IFields;
}

/**
 * Every list gets these two: Key, a stable id we generate (rows are found by it, never by
 * SharePoint's item id alone), and Removed, set instead of deleting a row so nothing is lost
 * and Undo can bring it back.
 */
export const KEY_COLUMN: IListColumn = { name: 'Key', label: 'Key', kind: 'text', indexed: true, hidden: true };
export const REMOVED_COLUMN: IListColumn = { name: 'Removed', label: 'Removed', kind: 'boolean', hidden: true };

interface IGraphPage<T> {
  value: T[];
  '@odata.nextLink'?: string;
}

interface IGraphItem {
  id: string;
  fields: IFields;
}

function statusOf(error: unknown): number | undefined {
  return (error as { statusCode?: number } | undefined)?.statusCode;
}

/** The column definition Microsoft Graph expects. */
export function columnDefinition(c: IListColumn): object {
  const base: { name: string; displayName: string; indexed?: boolean; hidden?: boolean } = { name: c.name, displayName: c.label };
  if (c.indexed) {
    base.indexed = true;
  }
  if (c.hidden) {
    base.hidden = true;
  }
  switch (c.kind) {
    case 'note': return { ...base, text: { allowMultipleLines: true, linesForEditing: 4 } };
    case 'date': return { ...base, dateTime: { format: 'dateOnly' } };
    case 'dateTime': return { ...base, dateTime: { format: 'dateTime' } };
    case 'number': return { ...base, number: {} };
    case 'boolean': return { ...base, boolean: {} };
    case 'choice': return { ...base, choice: { choices: c.choices ?? [], displayAs: 'dropDownMenu' } };
    default: return { ...base, text: {} };
  }
}

/** A value for an OData filter: strings quoted (with quotes doubled), the rest as they are. */
function literal(v: string | number | boolean): string {
  return typeof v === 'string' ? `'${v.replace(/'/g, "''")}'` : String(v);
}

/** Dates are stored as YYYY-MM-DD in our code; SharePoint gives them back as full timestamps. */
function fromSharePoint(c: IListColumn | undefined, v: FieldValue): FieldValue {
  if (c && c.kind === 'date' && typeof v === 'string') {
    return v.slice(0, 10);
  }
  return v;
}

function toSharePoint(c: IListColumn | undefined, v: FieldValue): FieldValue | null {
  if (v === undefined || v === '') {
    return null; // Clears the value.
  }
  if (c && c.kind === 'date' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return `${v}T00:00:00Z`;
  }
  return v;
}

/**
 * One SharePoint list in the firm's site, created the first time it's needed. This is where the
 * firm's records live (clients, applications, checklists, deadlines), so they stay in the firm's
 * own Microsoft 365. Rows are never deleted: they're marked Removed.
 *
 * Writes go through RecordWriter, which also logs every change in History so it can be undone.
 */
export class ListStore {
  private _listId: string | undefined;
  private _ensured: boolean = false;
  private readonly _columns: { [name: string]: IListColumn } = {};

  /**
   * @param _graph reads (the everyday sign-in is enough).
   * @param _write creates lists and columns and writes rows (needs site-management permission,
   * asked for on the first write); the same client as `_graph` when one can do both.
   */
  constructor(
    private readonly _graph: IGraphClient,
    private readonly _write: IGraphClient,
    private readonly _siteId: string,
    public readonly schema: IListSchema,
    private readonly _newKey: () => string = () => crypto.randomUUID()
  ) {
    for (const c of this.allColumns()) {
      this._columns[c.name] = c;
    }
  }

  public allColumns(): IListColumn[] {
    return [...this.schema.columns, KEY_COLUMN, REMOVED_COLUMN];
  }

  /** Finds the list, or creates it, and adds any columns it's missing. Safe to call often. */
  public async ensure(): Promise<string> {
    if (this._listId && this._ensured) {
      return this._listId;
    }
    let listId: string | undefined = this._listId ?? await this._find();
    if (!listId) {
      const created: { id: string } = await this._write.api(`/sites/${this._siteId}/lists`).post({
        displayName: this.schema.urlName, // Sets the address; the title is changed below.
        list: { template: 'genericList' },
        columns: this.allColumns().map(columnDefinition)
      });
      listId = created.id;
      await this._write.api(`/sites/${this._siteId}/lists/${listId}`).patch({ displayName: this.schema.title }).catch(() => undefined);
      await this._labelTitleColumn(listId).catch(() => undefined);
    } else {
      await this._ensureColumns(listId);
    }
    this._listId = listId;
    this._ensured = true;
    return listId;
  }

  /** Whether the list exists yet (set up by the firm setup); reading never creates it. */
  public async exists(): Promise<boolean> {
    return !!(await this._readable());
  }

  /** Rows whose indexed columns equal the given values (Removed rows left out unless asked for). */
  public async query(where: { [column: string]: string | number | boolean }, includeRemoved: boolean = false): Promise<IRecord[]> {
    const listId: string | undefined = await this._readable();
    if (!listId) {
      return []; // Not set up yet: nothing to read.
    }
    const filter: string = Object.keys(where).map(c => `fields/${c} eq ${literal(where[c])}`).join(' and ');
    let url: string | undefined = `/sites/${this._siteId}/lists/${listId}/items?$expand=fields&$top=500${filter ? `&$filter=${encodeURIComponent(filter)}` : ''}`;
    const out: IRecord[] = [];
    while (url) {
      const page: IGraphPage<IGraphItem> = await this._graph.api(url).get();
      for (const item of page.value) {
        const r: IRecord = this._record(item);
        if (includeRemoved || r.fields.Removed !== true) {
          out.push(r);
        }
      }
      url = page['@odata.nextLink'];
    }
    return out;
  }

  /** Every row (for small lists only, such as Clients and Applications). */
  public all(includeRemoved: boolean = false): Promise<IRecord[]> {
    return this.query({}, includeRemoved);
  }

  public async get(key: string): Promise<IRecord | undefined> {
    return (await this.query({ Key: key }, true))[0];
  }

  /** The current values of some columns of one row, by SharePoint item id (used to check before undoing). */
  public async fieldsOf(itemId: string, columns: string[]): Promise<IFields | undefined> {
    const listId: string | undefined = await this._readable();
    if (!listId) {
      return undefined;
    }
    try {
      const item: IGraphItem = await this._graph.api(`/sites/${this._siteId}/lists/${listId}/items/${itemId}?$expand=fields`).get();
      const r: IRecord = this._record(item);
      const out: IFields = {};
      for (const c of columns) {
        out[c] = r.fields[c];
      }
      return out;
    } catch (error) {
      if (statusOf(error) === 404) {
        return undefined;
      }
      throw error;
    }
  }

  /** Adds a row with a new Key (or the Key given in `fields`, when a row moves between lists). Use RecordWriter.create to have it logged in History. */
  public async create(fields: IFields): Promise<IRecord> {
    const listId: string = await this.ensure();
    const key: string = typeof fields.Key === 'string' && fields.Key ? fields.Key : this._newKey();
    const body: { [c: string]: FieldValue | null } = {};
    for (const c of Object.keys(fields)) {
      const v = toSharePoint(this._columns[c], fields[c]);
      if (v !== null) {
        body[c] = v;
      }
    }
    body.Key = key;
    const created: IGraphItem = await this._write.api(`/sites/${this._siteId}/lists/${listId}/items`).post({ fields: body });
    return { id: created.id, key, fields: { ...fields, Key: key } };
  }

  /** Changes some values of one row. Use RecordWriter.update to have it logged in History. */
  public async update(itemId: string, fields: IFields): Promise<void> {
    const listId: string = await this.ensure();
    const body: { [c: string]: FieldValue | null } = {};
    for (const c of Object.keys(fields)) {
      body[c] = toSharePoint(this._columns[c], fields[c]);
    }
    await this._write.api(`/sites/${this._siteId}/lists/${listId}/items/${itemId}/fields`).patch(body);
  }

  /** The list's id for reading: found once, never created here. */
  private async _readable(): Promise<string | undefined> {
    if (!this._listId) {
      this._listId = await this._find();
    }
    return this._listId;
  }

  private _record(item: IGraphItem): IRecord {
    const fields: IFields = {};
    for (const name of Object.keys(item.fields ?? {})) {
      if (name === 'Title' || this._columns[name]) {
        fields[name] = fromSharePoint(this._columns[name], item.fields[name]);
      }
    }
    return { id: item.id, key: String(fields.Key ?? ''), fields };
  }

  private _find(): Promise<string | undefined> {
    // By title first; the address covers a renamed list.
    return findListId(this._graph, this._siteId, [this.schema.title, this.schema.urlName]);
  }


  private async _ensureColumns(listId: string): Promise<void> {
    const existing: IGraphPage<{ id: string; name: string; indexed?: boolean }> = await this._graph.api(`/sites/${this._siteId}/lists/${listId}/columns?$select=id,name,indexed`).get();
    const have: { [name: string]: { id: string; indexed?: boolean } } = {};
    for (const c of existing.value) {
      have[c.name] = c;
    }
    for (const c of this.allColumns()) {
      if (!have[c.name]) {
        await this._write.api(`/sites/${this._siteId}/lists/${listId}/columns`).post(columnDefinition(c));
      } else if (c.indexed && !have[c.name].indexed) {
        // A column we filter on that an earlier version didn't index.
        await this._write.api(`/sites/${this._siteId}/lists/${listId}/columns/${have[c.name].id}`).patch({ indexed: true });
      }
    }
  }

  private async _labelTitleColumn(listId: string): Promise<void> {
    const columns: IGraphPage<{ id: string; name: string }> = await this._graph.api(`/sites/${this._siteId}/lists/${listId}/columns?$select=id,name`).get();
    const title: { id: string } | undefined = columns.value.filter(c => c.name === 'Title')[0];
    if (title) {
      await this._write.api(`/sites/${this._siteId}/lists/${listId}/columns/${title.id}`).patch({ displayName: this.schema.titleLabel });
    }
  }
}
